import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Divider
} from '@mui/material';
import {
    Quiz,
    EmojiEvents,
    TipsAndUpdates,
    Close,
    CheckCircle
} from '@mui/icons-material';
import StorageHelper from '../utils/storageHelper';

interface GameInstructionsProps {
    open: boolean;
    onClose: () => void;
}

const GameInstructions: React.FC<GameInstructionsProps> = ({ open, onClose }) => {
    const handleGotIt = () => {
        // Save to storage that user has seen instructions
        StorageHelper.setItem('famousInitials_hasSeenInstructions', 'true');
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { 
                    borderRadius: 2,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <Quiz color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">How to Play Famous Initials</Typography>
                    </Box>
                    <Button onClick={onClose} color="inherit" size="small">
                        <Close />
                    </Button>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {/* Quick Overview */}
                <Paper elevation={1} sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                        <strong>Goal:</strong> Identify the famous person using only their initials.
                        We use the name they're most famous for (e.g., "BP" = "Brad Pitt").
                        They can be a real person or a fictional character!
                    </Typography>
                </Paper>

                {/* Scoring */}
                <Box mb={1}>
                    <Typography variant="subtitle1" gutterBottom color="primary">
                        <EmojiEvents sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                        Scoring (Golf Style - Lower is Better):
                    </Typography>
                    <Stack spacing={1}>
                        <Typography variant="body2">
                            • Each question costs <strong>10 points</strong>
                        </Typography>
                        <Typography variant="body2">
                            • Each guess costs <strong>10 points</strong>
                        </Typography>
                    </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Steps */}
                <Box mb={2}>
                    <Typography variant="subtitle1" gutterBottom color="primary">
                        <CheckCircle sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                        Quick Steps:
                    </Typography>
                    <List dense sx={{ py: 0 }}>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Typography variant="body2" color="primary" fontWeight="bold">1</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Ask questions in any order."
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Typography variant="body2" color="primary" fontWeight="bold">2</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Unlock hints after asking all questions."
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Typography variant="body2" color="primary" fontWeight="bold">3</Typography>
                            </ListItemIcon>
                            <ListItemText
                                primary="Enter their most famous name."
                                
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption', fontStyle: 'italic' }}
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <Typography variant="body2" color="primary" fontWeight="bold">4</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Click 'Guess' to submit your answer."
                                primaryTypographyProps={{ variant: 'body2' }}
                            />
                        </ListItem>
                    </List>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    onClick={handleGotIt}
                    variant="contained"
                    color="primary"
                    size="large"
                >
                    Got it! Let's Play
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GameInstructions;